import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import type { ListFilterSummary } from './listFilterSummary';
import type { ListPositionLabel } from './listPosition';
import { ListPositionIndicator } from './ListPositionIndicator';

interface PatientStepButtonsProps {
    // Absent at either end of the filtered list.
    onPrevious?: () => void;
    onNext?: () => void;
    // No position, no middle segment: an older server, or still loading.
    position?: ListPositionLabel | null;
    listSummary: ListFilterSummary;
}

// One bordered group: buttons at the ends, position in the middle, divide-x draws the seams.
// Disabled fades the text, not opacity, so the seam a button carries stays visible.
const STEPPER_CLASS =
    'inline-flex h-8 items-stretch divide-x divide-gray-200 overflow-hidden rounded-lg border border-gray-200 bg-white';

const STEP_BUTTON_CLASS =
    'inline-flex items-center gap-1 px-2.5 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-gray-400 disabled:cursor-not-allowed disabled:text-gray-300 disabled:hover:bg-white';

export function PatientStepButtons({
    onPrevious,
    onNext,
    position,
    listSummary
}: PatientStepButtonsProps) {
    return (
        <div role="group" aria-label="Step through the list" className={STEPPER_CLASS}>
            <button
                type="button"
                onClick={onPrevious}
                disabled={!onPrevious}
                aria-label="Previous patient"
                className={STEP_BUTTON_CLASS}
            >
                <ChevronLeftIcon aria-hidden="true" className="h-3.5 w-3.5" />
                Previous
            </button>
            {position && <ListPositionIndicator label={position} summary={listSummary} />}
            <button
                type="button"
                onClick={onNext}
                disabled={!onNext}
                aria-label="Next patient"
                className={STEP_BUTTON_CLASS}
            >
                Next
                <ChevronRightIcon aria-hidden="true" className="h-3.5 w-3.5" />
            </button>
        </div>
    );
}
